import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';

// Global Workspaces
// @ts-ignore
import { ErrorBoundary } from 'workspaces/redux-error-boundary';

// Global Components
// @ts-ignore
import ErrorBoundaryAlert from 'components/ErrorBoundaryAlert';

import RoleButton from 'components/RoleButton';
import {
    ADMIN,
    MANAGER,
    ASSISTANT_MANAGER
} from 'components/RoleGate/constants';
// @ts-ignore
import Online from 'components/Online';
import PageTitleAndFilters from 'components/PageTitleAndFilters';
import PageTitleAndFiltersPusher from 'components/PageTitleAndFiltersPusher';

// @ts-ignore
import RoleModal from 'components/RoleModal';
// @ts-ignore
import PageWrapper from 'components/PageWrapper';
import SpraySchemaExistsGate from 'components/SpraySchemaExistsGate';
import WorksheetForm from 'components/WorksheetForm';
import ViewWorksheetButton from 'components/ViewWorksheetButton';

// Local Components
import ActiveTableRowsExistGate from 'components/ActiveTableRowsExistGate';
import PrintWorksheetModal from 'components/PrintWorksheetModal';
import {
    WORKSHEET_TYPE_CROP_SPRAY,
    WORKSHEET_TYPE_WEED_SPRAY
} from 'services/worksheet/constants';
import { VIEW_WORKSHEET_MODAL } from 'components/WorksheetForm/constants';
import { SPRAY_EVENT_TYPE_WEED } from 'services/sprayEventType/constants';
import { SprayEventType } from 'services/sprayEventType/types';
import { setSprayEventType } from 'services/sprayEventType/actions';
import SprayEventTypeWeedGate from 'components/SprayEventTypeWeedGate';
import SprayEventTypeCropGate from 'components/SprayEventTypeCropGate';

import EventsCurrentFilter from 'components/PageTitleAndFilters/components/EventsCurrentFilter';
import { CREATE_SPRAY_EVENT_MODAL } from './constants';
import SprayEventsRequest from './components/SprayEventsRequest';
import DeleteSprayEventModal from './components/DeleteSprayEventModal';
import SprayEventsVirtualizedTable from './components/SprayEventsVirtualizedTable';
import SprayEventEditModal from './components/SprayEventEditModal';
import SprayEventHasIncompleteEventsGate from './components/SprayEventHasIncompleteEventsGate';
import CreateSprayEventModal from './components/CreateSprayEventModal';
import CopySprayEventModal from './components/CopySprayEventModal';
import CompleteSprayEventModal from './components/CompleteSprayEventModal';
import CopySprayEventButton from './components/CopySprayEventButton';
import DeleteSprayEventButton from './components/DeleteSprayEventButton';
import CompleteSprayEventButton from './components/CompleteSprayEventButton';
import UncompleteSprayEventModal from './components/UncompleteSprayEventModal';

const { REACT_APP_PROJECT_NAME } = process.env;

interface Props {
    sprayEventType: SprayEventType;
}

const Diary = (props: Props): React.ReactElement => {
    const { sprayEventType } = props;

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setSprayEventType(sprayEventType));
    }, [dispatch, sprayEventType]);

    const isWeedSpray = sprayEventType === SPRAY_EVENT_TYPE_WEED;

    return (
        <ErrorBoundary
            namespace="Spray"
            FallbackComponent={<ErrorBoundaryAlert />}
        >
            <SpraySchemaExistsGate>
                <React.Fragment>
                    <Helmet>
                        <title>
                            {isWeedSpray ? 'Weed' : 'Crop'} Diary -{' '}
                            {REACT_APP_PROJECT_NAME}
                        </title>
                    </Helmet>
                    <Online>
                        <SprayEventsRequest />
                    </Online>
                    <PageTitleAndFilters
                        title={`${isWeedSpray ? 'Weed' : 'Crop'} Diary`}
                        CustomSearch={<EventsCurrentFilter />}
                        Button={
                            <React.Fragment>
                                <ViewWorksheetButton
                                    worksheetType={
                                        isWeedSpray
                                            ? WORKSHEET_TYPE_WEED_SPRAY
                                            : WORKSHEET_TYPE_CROP_SPRAY
                                    }
                                />
                                <RoleButton
                                    requiredRoles={[
                                        ADMIN,
                                        MANAGER,
                                        ASSISTANT_MANAGER
                                    ]}
                                    params={{
                                        [CREATE_SPRAY_EVENT_MODAL]: true
                                    }}
                                    buttonProps={{
                                        secondary: true,
                                        style: { marginLeft: 8 }
                                    }}
                                >
                                    Schedule Spray
                                </RoleButton>
                            </React.Fragment>
                        }
                        FirstActionButton={<CopySprayEventButton />}
                        SecondActionButton={
                            <ActiveTableRowsExistGate>
                                <SprayEventHasIncompleteEventsGate>
                                    <CompleteSprayEventButton />
                                </SprayEventHasIncompleteEventsGate>
                            </ActiveTableRowsExistGate>
                        }
                        ThirdActionButton={
                            <ActiveTableRowsExistGate>
                                <SprayEventHasIncompleteEventsGate>
                                    <DeleteSprayEventButton />
                                </SprayEventHasIncompleteEventsGate>
                            </ActiveTableRowsExistGate>
                        }
                        currentEntity
                        currentSeason
                    />
                    <PageWrapper top={0} bottom={50}>
                        <PageTitleAndFiltersPusher>
                            <SprayEventsVirtualizedTable tableId="sprayEvents" />
                        </PageTitleAndFiltersPusher>
                    </PageWrapper>
                    <CreateSprayEventModal />
                    <SprayEventEditModal />
                    <DeleteSprayEventModal />
                    <CopySprayEventModal />
                    <CompleteSprayEventModal fullscreen={!isWeedSpray} />
                    <UncompleteSprayEventModal />
                    <SprayEventTypeCropGate>
                        <React.Fragment>
                            <RoleModal
                                requiredRoles={[
                                    ADMIN,
                                    MANAGER,
                                    ASSISTANT_MANAGER
                                ]}
                                param={VIEW_WORKSHEET_MODAL}
                                ignoreSeason
                            >
                                <WorksheetForm
                                    worksheetType={WORKSHEET_TYPE_CROP_SPRAY}
                                />
                            </RoleModal>
                            <PrintWorksheetModal
                                worksheetType={WORKSHEET_TYPE_CROP_SPRAY}
                            />
                        </React.Fragment>
                    </SprayEventTypeCropGate>
                    <SprayEventTypeWeedGate>
                        <SprayEventTypeWeedGate>
                            <React.Fragment>
                                <RoleModal
                                    requiredRoles={[
                                        ADMIN,
                                        MANAGER,
                                        ASSISTANT_MANAGER
                                    ]}
                                    param={VIEW_WORKSHEET_MODAL}
                                    ignoreSeason
                                >
                                    <WorksheetForm
                                        worksheetType={
                                            WORKSHEET_TYPE_WEED_SPRAY
                                        }
                                    />
                                </RoleModal>
                                <PrintWorksheetModal
                                    worksheetType={WORKSHEET_TYPE_WEED_SPRAY}
                                />
                            </React.Fragment>
                        </SprayEventTypeWeedGate>
                    </SprayEventTypeWeedGate>
                </React.Fragment>
            </SpraySchemaExistsGate>
        </ErrorBoundary>
    );
};

export default Diary;
